import React from 'react';
import PageTabs from './PageTabs';
import PageConfig from './PageConfig';
import EmptyCanvas from './EmptyCanvas';
import ElementCard from './ElementCard';
import ConfiguratorPreviewLayout from './ConfiguratorPreviewLayout';
import { getFlowMode } from '../utils';
import { getEmbedPreviewFrameStyle, normalizeEmbedSettings } from '../../../utils/embedSnippet';

const TRANSPARENT_CHECKERBOARD = 'repeating-conic-gradient(#d1d5db 0% 25%, #f3f4f6 0% 50%) 50% / 16px 16px';

/**
 * CanvasArea component for GuiBuilder
 * The main canvas area that displays either the draft editor or live preview
 */
export default function CanvasArea({
    isPreviewMode,
    properties,
    pages,
    activePageIndex,
    setActivePageIndex,
    addPage,
    deletePage,
    updatePageField,
    isDragOverCanvas,
    handleFeatureDraggedFromPalette,
    handleDragOver,
    handleDragLeave,
    features,
    presentationOverrides = {},
    updatePresentationOverride = () => {},
    catalogContent = { features: {} },
    updateCatalogValue = () => {},
    moveElement,
    deleteElement,
    updateElementField,
    previewPageIndex = 0,
    setPreviewPageIndex = () => {},
    previewSelections = {},
    setPreviewSelections = () => {},
    previewAvailableDomains = null,
    showIntroFrontPage = true,
    setShowIntroFrontPage = () => {}
}) {
    const flowMode = getFlowMode(properties.designSettings);
    const embedSettings = normalizeEmbedSettings(properties.embed);
    const previewFrameStyle = getEmbedPreviewFrameStyle(embedSettings);

    return (
        <div style={{
            flex: 1,
            background: 'var(--bg-primary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: isPreviewMode ? '0' : '2.5rem',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
            minWidth: 0
        }}>
            {isPreviewMode ? (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    height: '100%',
                    minHeight: '500px',
                    width: '100%',
                    padding: '1.5rem',
                    boxSizing: 'border-box',
                    overflow: 'auto',
                    background: embedSettings.transparent ? TRANSPARENT_CHECKERBOARD : 'var(--bg-secondary)'
                }}>
                    <div
                        className="cfg-canvas"
                        style={previewFrameStyle}
                    >
                        <ConfiguratorPreviewLayout
                            properties={properties}
                            pages={pages}
                            previewPageIndex={previewPageIndex}
                            setPreviewPageIndex={setPreviewPageIndex}
                            previewSelections={previewSelections}
                            setPreviewSelections={setPreviewSelections}
                            previewAvailableDomains={previewAvailableDomains}
                            features={features}
                            showIntroFrontPage={showIntroFrontPage}
                            setShowIntroFrontPage={setShowIntroFrontPage}
                        />
                    </div>
                </div>
            ) : (
                /* DRAFT EDITOR MODE */
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1.5rem',
                        width: '100%',
                        backgroundColor: isDragOverCanvas ? 'var(--bg-secondary)' : 'transparent',
                        border: isDragOverCanvas ? '2px dashed var(--text-primary)' : 'none',
                        borderRadius: isDragOverCanvas ? '8px' : '0',
                        padding: isDragOverCanvas ? '1rem' : '0',
                        transition: 'all 0.2s ease'
                    }}
                    onDrop={handleFeatureDraggedFromPalette}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                >
                    {/* Page Tabs Manager */}
                    <PageTabs
                        pages={pages}
                        activePageIndex={activePageIndex}
                        setActivePageIndex={setActivePageIndex}
                        addPage={addPage}
                        deletePage={deletePage}
                        flowMode={flowMode}
                    />

                    {/* Page Config Details */}
                    <PageConfig
                        pages={pages}
                        activePageIndex={activePageIndex}
                        updatePageField={updatePageField}
                        flowMode={flowMode}
                    />

                    {/* Empty Canvas Indicator */}
                    {pages[activePageIndex]?.elements?.length === 0 && <EmptyCanvas />}

                    {/* Render Active Page Elements */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {pages[activePageIndex]?.elements?.map((el, index) => {
                            const isFirst = index === 0;
                            const isLast = index === pages[activePageIndex].elements.length - 1;

                            return (
                                <ElementCard
                                    key={el.id}
                                    el={el}
                                    index={index}
                                    isFirst={isFirst}
                                    isLast={isLast}
                                    features={features}
                                    presentationOverrides={presentationOverrides}
                                    updatePresentationOverride={updatePresentationOverride}
                                    catalogContent={catalogContent}
                                    updateCatalogValue={updateCatalogValue}
                                    moveElement={moveElement}
                                    deleteElement={deleteElement}
                                    updateElementField={updateElementField}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

CanvasArea.displayName = 'GuiBuilder.CanvasArea';
